/* ==========================================================================
   ProTrade Commerce — theme.js
   Core behaviours: header, navigation, drawers, quantity, variants,
   sticky add-to-cart, wishlist, facets, utilities.
   No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  const T = (window.theme = window.theme || {});
  T.utils = T.utils || {};

  const $  = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  const debounce = (fn, wait = 200) => {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const throttle = (fn, limit = 100) => {
    let waiting = false;
    return function (...args) {
      if (waiting) return;
      fn.apply(this, args);
      waiting = true;
      setTimeout(() => { waiting = false; }, limit);
    };
  };

  /**
   * Format a price in cents using the shop's money format string.
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || T.settings.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    let value = '';

    function pad(number, size) {
      let s = String(number);
      while (s.length < size) s = '0' + s;
      return s;
    }

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cts = parts[1] ? decimal + parts[1] : '';
      return dollars + cts;
    }

    const match = fmt.match(placeholder);
    switch (match && match[1]) {
      case 'amount':                 value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals':     value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator':     value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator':     value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  function trapFocus(container, elementToFocus) {
    const focusable = $$(
      'a[href], button:not([disabled]), textarea, input:not([type="hidden"]):not([disabled]), select, details, [tabindex]:not([tabindex="-1"])',
      container
    ).filter((el) => el.offsetParent !== null);

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function handler(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last && last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first && first.focus();
      }
    }

    container.addEventListener('keydown', handler);
    container._releaseFocusTrap = () => container.removeEventListener('keydown', handler);
    (elementToFocus || first || container).focus({ preventScroll: true });
  }

  function releaseFocus(container) {
    if (container && container._releaseFocusTrap) container._releaseFocusTrap();
  }

  T.utils = { $, $$, debounce, throttle, formatMoney, trapFocus, releaseFocus };

  /* -------------------------------------------------------- scroll lock */

  let scrollY = 0;
  let lockCount = 0;

  function lockScroll() {
    if (lockCount === 0) {
      scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add('scroll-locked');
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
    lockCount += 1;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove('scroll-locked');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    }
  }

  T.lockScroll = lockScroll;
  T.unlockScroll = unlockScroll;

  /* ------------------------------------------------------------ overlay */

  const overlay = $('[data-overlay]');
  let overlayUsers = 0;

  function showOverlay() {
    if (!overlay) return;
    overlayUsers += 1;
    overlay.hidden = false;
    requestAnimationFrame(() => overlay.classList.add('is-visible'));
  }

  function hideOverlay() {
    if (!overlay) return;
    overlayUsers = Math.max(0, overlayUsers - 1);
    if (overlayUsers > 0) return;
    overlay.classList.remove('is-visible');
    setTimeout(() => { if (overlayUsers === 0) overlay.hidden = true; }, 350);
  }

  /* ------------------------------------------------------------- drawer */

  const openDrawers = new Set();

  function openDrawer(id, trigger) {
    const drawer = document.getElementById(id);
    if (!drawer || drawer.classList.contains('is-open')) return;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer._trigger = trigger || document.activeElement;
    openDrawers.add(drawer);
    showOverlay();
    lockScroll();
    setTimeout(() => trapFocus(drawer, $('[data-drawer-close]', drawer)), 60);
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id } }));
  }

  function closeDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    releaseFocus(drawer);
    openDrawers.delete(drawer);
    hideOverlay();
    unlockScroll();
    if (drawer._trigger && document.body.contains(drawer._trigger)) {
      drawer._trigger.focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('drawer:close', { detail: { id: drawer.id } }));
  }

  function closeAllDrawers() {
    Array.from(openDrawers).forEach(closeDrawer);
  }

  T.openDrawer = openDrawer;
  T.closeDrawer = closeDrawer;
  T.closeAllDrawers = closeAllDrawers;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const id = opener.getAttribute('data-drawer-open');
      if (document.getElementById(id)) {
        event.preventDefault();
        openDrawer(id, opener);
      }
      return;
    }

    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('.drawer') || document.getElementById(closer.getAttribute('data-drawer-close')));
    }
  });

  if (overlay) {
    overlay.addEventListener('click', () => {
      closeAllDrawers();
      const facets = $('.facets.is-open');
      if (facets) { facets.classList.remove('is-open'); hideOverlay(); unlockScroll(); }
      const search = $('.header-search.is-open');
      if (search) closeHeaderSearch();
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (openDrawers.size) { closeAllDrawers(); return; }
    const facets = $('.facets.is-open');
    if (facets) { facets.classList.remove('is-open'); hideOverlay(); unlockScroll(); return; }
    if ($('.header-search.is-open')) { closeHeaderSearch(); return; }
    const openMenu = $('.header__menu-item.is-open');
    if (openMenu) {
      openMenu.classList.remove('is-open');
      const link = $('.header__menu-link', openMenu);
      link && link.setAttribute('aria-expanded', 'false');
      link && link.focus();
    }
  });

  /* ------------------------------------------------------------- header */

  function initHeader() {
    const wrapper = $('[data-header-wrapper]');
    if (!wrapper) return;

    const announcement = $('.announcement-bar');
    const setOffsets = () => {
      document.documentElement.style.setProperty('--header-height', `${wrapper.offsetHeight}px`);
      if (announcement) {
        document.documentElement.style.setProperty('--announcement-height', `${announcement.offsetHeight}px`);
      }
    };
    setOffsets();
    window.addEventListener('resize', debounce(setOffsets, 150));

    const isTransparent = wrapper.classList.contains('header-wrapper--transparent');
    if (!isTransparent) return;

    const onScroll = throttle(() => {
      wrapper.classList.toggle('is-stuck', window.scrollY > 24);
    }, 80);

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --------------------------------------------------------- navigation */

  function initNavigation() {
    const items = $$('.header__menu-item.has-submenu');
    if (!items.length) return;

    const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

    items.forEach((item) => {
      const link = $('.header__menu-link', item);
      if (!link) return;
      let closeTimer;

      const open = () => {
        clearTimeout(closeTimer);
        items.forEach((other) => {
          if (other !== item) {
            other.classList.remove('is-open');
            const l = $('.header__menu-link', other);
            l && l.setAttribute('aria-expanded', 'false');
          }
        });
        item.classList.add('is-open');
        link.setAttribute('aria-expanded', 'true');
      };

      const close = (delay = 180) => {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => {
          item.classList.remove('is-open');
          link.setAttribute('aria-expanded', 'false');
        }, delay);
      };

      item.addEventListener('mouseenter', () => { if (isDesktop()) open(); });
      item.addEventListener('mouseleave', () => { if (isDesktop()) close(); });

      link.addEventListener('click', (event) => {
        if (!isDesktop()) return;
        // First tap/click opens the panel rather than following the link.
        if (!item.classList.contains('is-open')) {
          event.preventDefault();
          open();
        }
      });

      link.addEventListener('focus', () => { if (isDesktop()) open(); });
      item.addEventListener('focusout', (event) => {
        if (isDesktop() && !item.contains(event.relatedTarget)) close(0);
      });
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('.header__menu-item')) return;
      items.forEach((item) => {
        item.classList.remove('is-open');
        const l = $('.header__menu-link', item);
        l && l.setAttribute('aria-expanded', 'false');
      });
    });
  }

  /* ------------------------------------------------------- mobile menu */

  function initMobileMenu() {
    $$('[data-mobile-submenu-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const item = button.closest('.mobile-menu__item');
        if (!item) return;
        const isOpen = item.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }

  /* ------------------------------------------------------ header search */

  function openHeaderSearch(trigger) {
    const search = $('[data-header-search]');
    if (!search) return;
    search.classList.add('is-open');
    search.setAttribute('aria-hidden', 'false');
    search._trigger = trigger;
    const input = $('input[type="search"]', search);
    setTimeout(() => input && input.focus(), 120);
    $$('[data-search-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
  }

  function closeHeaderSearch() {
    const search = $('[data-header-search]');
    if (!search) return;
    search.classList.remove('is-open');
    search.setAttribute('aria-hidden', 'true');
    $$('[data-search-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    const results = $('[data-predictive-results]', search);
    if (results) results.hidden = true;
    if (search._trigger) search._trigger.focus({ preventScroll: true });
  }

  T.openHeaderSearch = openHeaderSearch;
  T.closeHeaderSearch = closeHeaderSearch;

  function initHeaderSearch() {
    $$('[data-search-toggle]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const search = $('[data-header-search]');
        if (!search) return;
        search.classList.contains('is-open') ? closeHeaderSearch() : openHeaderSearch(button);
      });
    });
    $$('[data-search-close]').forEach((button) => {
      button.addEventListener('click', (event) => { event.preventDefault(); closeHeaderSearch(); });
    });
  }

  /* ---------------------------------------------------------- quantity */

  function initQuantity(scope) {
    $$('[data-quantity]', scope || document).forEach((widget) => {
      if (widget._bound) return;
      widget._bound = true;

      const input = $('input', widget);
      if (!input) return;

      $$('[data-quantity-button]', widget).forEach((button) => {
        button.addEventListener('click', () => {
          const step = button.dataset.quantityButton === 'plus' ? 1 : -1;
          const min = parseInt(input.min || '0', 10);
          const max = input.max ? parseInt(input.max, 10) : Infinity;
          const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + step));
          if (next === parseInt(input.value, 10)) return;
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      input.addEventListener('change', () => {
        const min = parseInt(input.min || '0', 10);
        const max = input.max ? parseInt(input.max, 10) : Infinity;
        let value = parseInt(input.value, 10);
        if (isNaN(value)) value = min;
        input.value = Math.min(max, Math.max(min, value));
      });
    });
  }

  T.initQuantity = initQuantity;

  /* ----------------------------------------------------- variant picker */

  function initVariantPickers(scope) {
    $$('[data-variant-picker]', scope || document).forEach((picker) => {
      if (picker._bound) return;
      picker._bound = true;

      const sectionId = picker.dataset.section;
      const productUrl = picker.dataset.url;
      const dataEl = document.getElementById(`ProductVariants-${sectionId}`);
      if (!dataEl) return;

      let variants = [];
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      const form = document.getElementById(`ProductForm-${sectionId}`);
      const idInput = form ? $('input[name="id"]', form) : null;
      const submit = form ? $('[data-add-to-cart]', form) : null;
      const priceEl = $(`#ProductPrice-${sectionId}`);
      const skuEl = $(`#ProductSku-${sectionId}`);
      const inventoryEl = $(`#ProductInventory-${sectionId}`);
      const stickyPrice = $(`#StickyPrice-${sectionId}`);

      function currentOptions() {
        return $$('input[type="radio"]:checked, select', picker).map((el) => el.value);
      }

      function findVariant(options) {
        return variants.find((variant) =>
          variant.options.every((option, index) => option === options[index])
        );
      }

      function updateAvailability(options) {
        // Disable option values that produce no purchasable combination.
        $$('fieldset', picker).forEach((fieldset, optionIndex) => {
          $$('input[type="radio"]', fieldset).forEach((input) => {
            const test = options.slice();
            test[optionIndex] = input.value;
            const match = variants.find((variant) =>
              variant.options.every((option, i) => (i === optionIndex ? option === input.value : option === test[i]))
            );
            input.disabled = !match || !match.available;
          });
        });
      }

      function render() {
        const options = currentOptions();
        const variant = findVariant(options);
        updateAvailability(options);

        if (!variant) {
          if (submit) {
            submit.disabled = true;
            const label = $('.btn__label', submit);
            if (label) label.textContent = T.strings.unavailable;
          }
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (window.history.replaceState && productUrl) {
          const url = new URL(productUrl, window.location.origin);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        if (priceEl) priceEl.innerHTML = buildPriceMarkup(variant);
        if (stickyPrice) stickyPrice.innerHTML = formatMoney(variant.price);

        if (skuEl) {
          skuEl.textContent = variant.sku || '';
          skuEl.closest('[data-sku-wrapper]') &&
            (skuEl.closest('[data-sku-wrapper]').hidden = !variant.sku);
        }

        if (inventoryEl) {
          if (variant.available && variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= 10) {
            inventoryEl.hidden = false;
            inventoryEl.textContent = inventoryEl.dataset.template.replace('[count]', variant.inventory_quantity);
          } else {
            inventoryEl.hidden = true;
          }
        }

        if (submit) {
          submit.disabled = !variant.available;
          const label = $('.btn__label', submit);
          if (label) label.textContent = variant.available ? T.strings.addToCart : T.strings.soldOut;
        }

        // Jump the gallery to the variant's image.
        if (variant.featured_media_id) {
          const thumb = $(`[data-gallery-thumb][data-media-id="${variant.featured_media_id}"]`);
          if (thumb) thumb.click();
        }

        picker.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
      }

      function buildPriceMarkup(variant) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        let html = '';
        if (onSale) {
          html += `<span class="price__sale">${formatMoney(variant.price)}</span>`;
          html += `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`;
        } else {
          html += `<span class="price__regular">${formatMoney(variant.price)}</span>`;
        }
        if (priceEl) priceEl.classList.toggle('price--on-sale', !!onSale);
        return html;
      }

      picker.addEventListener('change', render);
      render();
    });
  }

  /* ------------------------------------------------------- sticky ATC */

  function initStickyAtc() {
    const sticky = $('[data-sticky-atc]');
    const form = $('[data-product-form]');
    if (!sticky || !form) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { rootMargin: '0px 0px -100% 0px', threshold: 0 }
    );
    observer.observe(form);

    const trigger = $('[data-sticky-submit]', sticky);
    if (trigger) {
      trigger.addEventListener('click', () => {
        const realSubmit = $('[data-add-to-cart]', form);
        realSubmit && realSubmit.click();
      });
    }
  }

  /* --------------------------------------------------------- gallery */

  function initGallery() {
    $$('[data-gallery]').forEach((gallery) => {
      const slides = $$('[data-gallery-slide]', gallery);
      const thumbs = $$('[data-gallery-thumb]', gallery);
      const track = $('[data-gallery-track]', gallery);
      if (!slides.length) return;

      function activate(index) {
        thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === index);
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        if (track) {
          track.scrollTo({ left: track.clientWidth * index, behavior: 'smooth' });
        }
      }

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', (event) => { event.preventDefault(); activate(index); });
      });

      if (track) {
        track.addEventListener('scroll', throttle(() => {
          const index = Math.round(track.scrollLeft / track.clientWidth);
          thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
        }, 120), { passive: true });
      }

      // Click-to-zoom with pointer-tracked transform origin.
      slides.forEach((slide) => {
        slide.addEventListener('click', () => slide.classList.toggle('is-zoomed'));
        slide.addEventListener('mousemove', (event) => {
          if (!slide.classList.contains('is-zoomed')) return;
          const rect = slide.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          const img = $('img', slide);
          if (img) img.style.transformOrigin = `${x}% ${y}%`;
        });
        slide.addEventListener('mouseleave', () => slide.classList.remove('is-zoomed'));
      });

      activate(0);
    });
  }

  /* --------------------------------------------------------- wishlist */

  const WISHLIST_KEY = 'protrade:wishlist';

  function readWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; }
    catch (e) { return []; }
  }

  function writeWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) { /* storage full or blocked */ }
  }

  function initWishlist() {
    const list = readWishlist();

    function paint(button) {
      const id = button.dataset.wishlist;
      button.setAttribute('aria-pressed', String(readWishlist().indexOf(id) !== -1));
    }

    $$('[data-wishlist]').forEach((button) => {
      paint(button);
      if (button._bound) return;
      button._bound = true;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const id = button.dataset.wishlist;
        const current = readWishlist();
        const index = current.indexOf(id);
        index === -1 ? current.push(id) : current.splice(index, 1);
        writeWishlist(current);
        paint(button);
        document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list: current } }));
      });
    });

    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list } }));
  }

  T.wishlist = { read: readWishlist, write: writeWishlist };

  /* ------------------------------------------------------------ facets */

  function initFacets() {
    const facets = $('[data-facets]');
    if (!facets) return;

    const form = $('form', facets);
    if (form) {
      form.addEventListener('change', debounce(() => {
        if (form.dataset.autoSubmit === 'false') return;
        form.submit();
      }, 400));
    }

    $$('[data-facets-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        facets.classList.add('is-open');
        showOverlay();
        lockScroll();
        trapFocus(facets);
      });
    });

    $$('[data-facets-close]').forEach((button) => {
      button.addEventListener('click', () => {
        facets.classList.remove('is-open');
        releaseFocus(facets);
        hideOverlay();
        unlockScroll();
      });
    });
  }

  function initSort() {
    $$('[data-sort-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  /* -------------------------------------------------- newsletter status */

  function initNewsletterFocus() {
    if (window.location.hash === '#newsletter-success') {
      const el = $('[data-newsletter-status]');
      el && el.focus();
    }
  }

  /* ------------------------------------------------------ share button */

  function initShare() {
    $$('[data-share-button]').forEach((button) => {
      button.addEventListener('click', async () => {
        const url = button.dataset.shareUrl || window.location.href;
        const title = button.dataset.shareTitle || document.title;
        if (navigator.share) {
          try { await navigator.share({ title, url }); } catch (e) { /* user dismissed */ }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          const label = $('.btn__label', button) || button;
          const original = label.textContent;
          label.textContent = button.dataset.copiedLabel || 'Copied';
          setTimeout(() => { label.textContent = original; }, 2000);
        } catch (e) { /* clipboard blocked */ }
      });
    });
  }

  /* -------------------------------------------- product recommendations */

  /**
   * `recommendations.products` is only populated on the recommendations route,
   * so the section renders a collection-based fallback first and upgrades to
   * Shopify's real recommendations once they load.
   */
  function initRecommendations() {
    const container = $('[data-related-products]');
    if (!container || !container.dataset.url) return;

    fetch(container.dataset.url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : Promise.reject()))
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('[data-related-products]');
        if (!fresh || !fresh.querySelector('[data-slider-slide]')) return;
        container.innerHTML = fresh.innerHTML;
        T.initSliders && T.initSliders(container);
        T.initReveal && T.initReveal(container);
        initWishlist();
      })
      .catch(() => { /* keep the server-rendered fallback */ });
  }

  /* ------------------------------------------------ theme editor events */

  function initEditorEvents() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    document.addEventListener('shopify:section:load', (event) => {
      initQuantity(event.target);
      initVariantPickers(event.target);
      initHeader();
      initNavigation();
      initMobileMenu();
      initHeaderSearch();
      initGallery();
      initWishlist();
      document.dispatchEvent(new CustomEvent('theme:section:load', { detail: { target: event.target } }));
    });

    // Only the cart drawer should auto-open on select. The header section also
    // contains a drawer (the mobile menu), which would otherwise cover the
    // preview whenever the merchant clicks Header in the editor.
    document.addEventListener('shopify:section:select', (event) => {
      const drawer = event.target.querySelector('[data-cart-drawer]');
      if (drawer) openDrawer(drawer.id);
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      const drawer = event.target.querySelector('[data-cart-drawer]');
      if (drawer) closeDrawer(drawer);
    });
  }

  /* --------------------------------------------------------------- init */

  function init() {
    initHeader();
    initNavigation();
    initMobileMenu();
    initHeaderSearch();
    initQuantity();
    initVariantPickers();
    initStickyAtc();
    initGallery();
    initWishlist();
    initFacets();
    initSort();
    initShare();
    initRecommendations();
    initNewsletterFocus();
    initEditorEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
